import React from 'react';
import { Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SectionWrapper } from '../SectionWrapper';
import { GradientTitle } from '../GradientTitle';

interface SubscriptionStatus {
  isActive: boolean;
  expiresAt: string | null;
  period: number;
  subscription_start_date: string | null;
}

interface SubscriptionStatusSectionProps {
  subscriptionStatus: SubscriptionStatus;
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Not started';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Not started';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const SubscriptionStatusSection: React.FC<SubscriptionStatusSectionProps> = ({
  subscriptionStatus
}) => {
  const { t } = useTranslation();

  const progressPercentage = subscriptionStatus.isActive && 
    subscriptionStatus.subscription_start_date && 
    subscriptionStatus.expiresAt
    ? Math.max(0, Math.min(100, 
        ((Date.now() - new Date(subscriptionStatus.subscription_start_date).getTime()) /
        (new Date(subscriptionStatus.expiresAt).getTime() - new Date(subscriptionStatus.subscription_start_date).getTime())) * 100
      ))
    : 0;

  return (
    <SectionWrapper>
      <div className="flex justify-between items-start">
        <GradientTitle
          title={t('PROFILE_SCREEN.SUBSCRIPTION_STATUS.TITLE')}
          caption={t('PROFILE_SCREEN.SUBSCRIPTION_STATUS.CAPTION')}
        />
        <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-pink rounded-xl shadow-[0_4px_12px_rgba(255,0,128,0.2)] text-white text-xs font-semibold tracking-[0.5px]">
          <Crown size={16} />
          <span>
            {subscriptionStatus.isActive 
              ? t('PROFILE_SCREEN.SUBSCRIPTION_STATUS.ACTIVE') 
              : t('PROFILE_SCREEN.SUBSCRIPTION_STATUS.INACTIVE')}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 my-4">
          <div className="bg-tg-secondary-bg p-4 rounded-xl flex flex-col gap-1 border border-pink-500/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]">
            <span className="text-[13px] text-tg-hint">
              {t('PROFILE_SCREEN.SUBSCRIPTION_STATUS.PERIOD')}
            </span>
            <span 
              className="text-xl font-semibold text-tg-text"
              style={{
                background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {subscriptionStatus.period || 0} {t('PROFILE_SCREEN.PREMIUM.MONTHS')}
            </span>
          </div>
          <div className="bg-tg-secondary-bg p-4 rounded-xl flex flex-col gap-1 border border-pink-500/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]">
            <span className="text-[13px] text-tg-hint">
              {t('PROFILE_SCREEN.SUBSCRIPTION_STATUS.EXPIRES')}
            </span>
            <span 
              className="text-xl font-semibold text-tg-text"
              style={{
                background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {subscriptionStatus.expiresAt ? formatDate(subscriptionStatus.expiresAt) : '—'}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2 p-4 bg-tg-secondary-bg rounded-xl border border-pink-500/10">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-tg-text">
              {t('PROFILE_SCREEN.SUBSCRIPTION_STATUS.PROGRESS')}
            </span>
            <span className="text-xs text-tg-hint">
              {formatDate(subscriptionStatus.subscription_start_date)} - {formatDate(subscriptionStatus.expiresAt)}
            </span>
          </div>
          <div className="h-1.5 bg-pink-500/10 rounded-sm overflow-hidden">
            <div 
              className="h-full rounded-sm transition-all duration-300"
              style={{ 
                width: `${progressPercentage}%`,
                background: subscriptionStatus.isActive 
                  ? 'linear-gradient(135deg, #FF0080, #FF6B00)' 
                  : 'var(--tg-theme-hint-color)'
              }} 
            />
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

